import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  effect,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subscription, catchError, map, of, switchMap, tap, timer } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CompanyNameCheckComponent } from '../../../../shared/company-name-check/company-name-check';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { AgoPipe } from '../../../../shared/pipes/ago.pipe';
import { EntityService } from '../../../../core/entity/entity.service';
import { EntityDetail } from '../../../../core/entity/entity.types';
import { AiConsentState, aiConsentBoxNeeded } from '../../../../core/attestations/ai-consent.types';
import { BusinessAiConsentService } from '../../../../core/attestations/business-ai-consent.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { AiConsentCheckboxComponent } from '../../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { BankAccountsStepComponent } from './bank-accounts-step/bank-accounts-step';
import { SummaryStepComponent } from './summary-step/summary-step';
import { CHECKLIST_DOCS, Step, requirementLabel } from './intake-checklist';
import { EncourageInterceptComponent } from './encourage-intercept/encourage-intercept';
import { SubmittedStatusComponent } from './submitted-status/submitted-status';
import { PreApplicationService } from '../../../../core/applications/pre-application.service';
import {
  DetectedAccount,
  PreApplication,
  PreApplicationDocType,
  PreApplicationRecommendationResult,
  PreApplicationReviewStatus,
} from '../../../../core/applications/pre-application.types';
import { RealtimeService } from '../../../../core/realtime/realtime.service';
import { DocAssessedPayload } from '../../../../core/realtime/realtime.types';
import { DocAssessStatus } from '../../../../core/status/doc-assess.types';
import {
  DocDropComponent,
  DocDropType,
  DocUpload,
  uploadErrorMessage,
} from '../../shared/doc-drop/doc-drop';
import { readError } from '../../../../core/http/read-error';

/** The step URL segments (…/applications/new/:step), for parsing the route. */
const STEP_IDS: readonly Step[] = ['bank', 'financials', 'offers'];

/**
 * The encouragement intercept's URL segment (…/applications/new/encourage). Not
 * a step: it sits between Step 2 and the summary, with Step 2 kept active in
 * the stepper behind it, and is refresh-safe through this segment.
 */
const INTERCEPT_SEGMENT = 'encourage';

/** Bucket order for grouping uploads (matches the checklist order). */
const PRE_APP_DOCS: DocDropType<PreApplicationDocType>[] = CHECKLIST_DOCS.map((d) => d.type);

/**
 * The "New application" page — one combined screen (rebuilt in place):
 *
 * A three-step guided intake:
 *
 * - **Step 1 — Bank statements (required):** the floor for every decision; we
 *   ask for 6–9 months and encourage more.
 * - **Step 2 — Financial information (recommended):** age analysis + financial
 *   statements — optional, but they strengthen the application.
 * - **Step 3 — Application summary:** a live recap of what was shared (per-bucket
 *   check states fed by the AI doc checks), "what happens next", and the
 *   Debtors Finance unlock panel — sharing an age analysis unlocks a
 *   "Submit & unlock Debtors Finance" track that records `df_intent` for Ops.
 *
 * The applicant shares what they have and submits — CapX still decides the
 * product(s). `df_intent` is an *interest* flag Ops already sees on its worklist,
 * not a product selection. The recommendation the front-door rules compute stays
 * an internal Ops signal, never shown here (N4): the unlock panel keys off the
 * client's own uploads ("you shared an age analysis"), says "may qualify", and
 * its facility figure is an indicative demo amount, not a computed offer. One
 * URL for everyone; agents reuse it.
 */
const CHECK_THE_NUMBER_LINE =
  'Please contact us at support@flow.capx.co.za to correct the registration number.';

@Component({
  selector: 'app-product-chooser',
  imports: [
    CompanyNameCheckComponent,
    NgTemplateOutlet,
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    IconComponent,
    StatusChipComponent,
    DocDropComponent,
    AgoPipe,
    BankAccountsStepComponent,
    SummaryStepComponent,
    AiConsentCheckboxComponent,
    EncourageInterceptComponent,
    SubmittedStatusComponent,
  ],
  templateUrl: './product-chooser.html',
  styleUrl: './product-chooser.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductChooserComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly preApp = inject(PreApplicationService);
  private readonly entityService = inject(EntityService);
  private readonly realtime = inject(RealtimeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';

  /**
   * The business this intake belongs to — read purely to name it in the
   * breadcrumb, the way every other entity-scoped screen does. `null` until it
   * loads (or if the read fails), in which case the crumb is simply omitted.
   */
  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly PRE_APP_DOCS = PRE_APP_DOCS;

  /** Step 2's checklist; Step 1 renders its own. */
  protected readonly FINANCIAL_DOCS = CHECKLIST_DOCS.filter((d) => d.step === 'financials');

  // --- Stepper ---------------------------------------------------------------
  protected readonly steps: { id: Step; label: string }[] = [
    { id: 'bank', label: 'Bank Statements' },
    { id: 'financials', label: 'Financial Information' },
    { id: 'offers', label: 'Application Summary' },
  ];
  protected readonly step = signal<Step>('bank');
  protected readonly currentIndex = computed(() =>
    this.steps.findIndex((s) => s.id === this.step()),
  );
  /** Progress as a whole percentage — drives the header bar + "N% Complete". */
  protected readonly progressPct = computed(() =>
    Math.round(((this.currentIndex() + 1) / this.steps.length) * 100),
  );

  protected stepState(id: Step): 'done' | 'active' | 'upcoming' {
    const i = this.steps.findIndex((s) => s.id === id);
    const current = this.currentIndex();
    if (i < current) return 'done';
    if (i === current) return 'active';
    return 'upcoming';
  }

  protected next(): void {
    // Leaving Step 2 with nothing in either financial bucket meets the
    // encouragement intercept first (master document); with either document
    // server-confirmed the summary follows directly.
    if (this.step() === 'financials' && !this.hasFinancialDocs()) {
      this.showIntercept();
      return;
    }
    const n = this.steps[this.currentIndex() + 1];
    if (n) this.setStep(n.id);
  }

  // --- Encouragement intercept (between Step 2 and the summary) --------------
  /**
   * True while the intercept is on screen. Step 2 stays the active step behind
   * it; any `setStep` (Back, a stepper marker, the intercept's own actions)
   * clears it. Cleared automatically if a financial document turns out to be
   * on file — a refresh onto `…/new/encourage` with documents present lands on
   * Step 2, never on a screen that says nothing was shared.
   */
  protected readonly intercept = signal(false);

  /** Either financial bucket has a server-confirmed upload. */
  protected readonly hasFinancialDocs = computed(
    () => this.docsIn('AGE_ANALYSIS').length > 0 || this.docsIn('FINANCIALS').length > 0,
  );

  /** Step 2's uploads, both buckets. */
  private readonly financialUploads = computed(() =>
    this.FINANCIAL_DOCS.flatMap((d) => this.uploadsByType().get(d.type.value) ?? []),
  );

  /**
   * A financial document is still uploading or being checked, so Step 2 can't be
   * left yet. A check the poll gave up on counts as finished, so a slow one can't
   * strand the client here.
   */
  protected readonly financialsSettling = computed(() =>
    this.financialUploads().some(
      (u) =>
        u.state === 'uploading' ||
        (u.state === 'done' &&
          (u.doc?.assess_status ?? 'PENDING') === 'PENDING' &&
          !this.verdictPollTimedOut()),
    ),
  );

  /**
   * The check flagged a financial document. Never a block: the check can be
   * wrong, and CapX reads every document shared, so the client may carry on.
   */
  protected readonly financialsFlagged = computed(() =>
    this.financialUploads().some(
      (u) => u.doc?.assess_status === 'PROBLEM' || u.doc?.assess_status === 'WARNING',
    ),
  );

  /** The intercept never stands over a record that does hold financial documents. */
  private readonly interceptGuard = effect(() => {
    if (this.intercept() && this.hasFinancialDocs()) {
      this.intercept.set(false);
    }
  });

  private showIntercept(): void {
    this.step.set('financials');
    this.intercept.set(true);
    this.router
      .navigate(['/entity', this.entityId, 'applications', 'new', INTERCEPT_SEGMENT], {
        queryParamsHandling: 'preserve',
      })
      .catch(() => undefined);
  }

  /** Intercept: carry on with bank data only. */
  protected submitWithoutFinancials(): void {
    this.setStep('offers');
  }

  /** Intercept: go back and add financial documents. */
  protected uploadFinancials(): void {
    this.setStep('financials');
  }

  protected back(): void {
    const prev = this.steps[this.currentIndex() - 1];
    if (prev) this.setStep(prev.id);
  }

  /** Jump straight to a step — only backwards (to a completed step). */
  protected goToStep(id: Step): void {
    if (this.stepState(id) === 'done') this.setStep(id);
  }

  /**
   * Move to a step: update the view immediately and mirror it into the URL
   * (…/applications/new/:step) so the address bar tracks progress and the
   * browser's back/forward buttons walk the steps.
   */
  private setStep(id: Step): void {
    this.intercept.set(false);
    this.step.set(id);
    this.router
      .navigate(['/entity', this.entityId, 'applications', 'new', id], {
        queryParamsHandling: 'preserve',
      })
      .catch(() => undefined);
  }

  /**
   * The client pressed Apply for funding (`?start_new=1`): show a clean intake
   * and open a new record on first engagement, rather than resuming an editable
   * one. Held until that record exists, so moving between steps keeps it.
   */
  private startNew = this.route.snapshot.queryParamMap.get('start_new') === '1';

  /** The new record exists: from here on, a refresh must resume it, not start another. */
  private consumeStartNew(): void {
    if (!this.startNew) return;
    this.startNew = false;
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { start_new: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .catch(() => undefined);
  }

  protected readonly preAppId = signal<string | null>(null);
  protected readonly uploads = signal<DocUpload<PreApplicationDocType>[]>([]);
  protected readonly deletingIds = signal<Set<string>>(new Set());
  protected readonly isSubmitting = signal(false);
  protected readonly serverError = signal<string | null>(null);
  /** *Is this your business?*, where it reached submission unanswered (DOC-93 §1). */
  protected readonly nameToConfirm = signal<string | null>(null);
  private nameCheckWithDf = false;

  /** True while the initial "what's the current state?" fetch is in flight. */
  protected readonly loading = signal(true);
  /**
   * The current user isn't authorised to act for this business (the initial
   * read came back 403). We show a dead-end "can't act here" panel rather than
   * a usable wizard whose every upload/submit would fail — this is what made a
   * genuine permissions problem look like a broken upload.
   */
  protected readonly forbidden = signal(false);
  /**
   * The server-side review state of the entity's current pre-application, or
   * ``null`` when none exists yet (a fresh intake). Drives the whole view: the
   * intake form, the "under review" pending panel, the more-info / declined
   * prompts (which reopen the form), and the proceeded outcome.
   */
  protected readonly reviewStatus = signal<PreApplicationReviewStatus | null>(null);
  /** The plain-language Ops message shown on DECLINED (N4). */
  protected readonly clientReason = signal('');
  /**
   * §A1.4 (FLOW-192 outcome 2): the door referred this business to a person —
   * a trust, an NPO, or no CIPC registration. The notice replaces the wizard:
   * no document step, no submit, no product named, no figure. Read from the
   * door on load, before any upload could run an AI check on the file.
   */
  protected readonly referred = signal(false);
  /** The referral's client copy, in the server's words (N4: the outcome only). */
  protected readonly referralCopy = signal<{ headline: string; reason: string } | null>(null);
  /**
   * The client's recorded Debtors Finance interest (`df_intent` on the record —
   * Ops sees it on the worklist). Set by which submit track they choose; after a
   * reload it's rehydrated from the server so the submitted recap stays truthful.
   */
  protected readonly dfIntent = signal(false);
  /**
   * When the loaded intake was started — a returning client is told how old it
   * is, not the date (FLOW-394, as on the Kickstart / Debtors Finance drafts).
   * `null` on a fresh intake, which has nothing to resume.
   */
  protected readonly startedAt = signal<string | null>(null);

  private readonly auth = inject(AuthService);
  private readonly businessConsent = inject(BusinessAiConsentService);
  /**
   * The business's consent (FLOW-297, `§A6.7.2`), for before an intake is
   * loaded — so a second person at a business that has consented is not asked.
   * Its own subscription, like `loadEntity`; a failed read falls back to asking.
   */
  protected readonly businessAiConsent = toSignal(
    this.businessConsent.getState(this.entityId).pipe(
      map((r) => r.ai_consent),
      catchError(() => of(null)),
    ),
    { initialValue: null },
  );

  /**
   * POPIA consent to AI-assisted assessment of this intake.
   *
   * Gates the FIRST upload rather than the submit (which is where Kickstart
   * asks): a pre-application document is sent to the AI provider the moment it
   * lands, so by submit time the transfer has already happened. Hydrated from
   * the server's `ai_processing_consent` so a client continuing a consented
   * intake isn't asked twice.
   *
   * Since the sign-up capture (2026-09-22) the consent is given once on the
   * register form and stands for every application: the server derives this
   * intake's consent from it, and the box below is the FALLBACK — rendered only
   * when no standing consent exists, or the consent on THIS intake was
   * withdrawn (the standing one does not ride over a withdrawal made here).
   */
  protected readonly aiConsent = signal(false);

  /** No question needed: the business's consent stands, or a sign-up tick can be carried to it. */
  protected readonly standingAiConsentApplies = computed(
    () =>
      !aiConsentBoxNeeded(
        this.aiConsentState() ?? this.businessAiConsent(),
        this.auth.hasStandingAiConsent(),
      ),
  );
  protected readonly showAiConsentBox = computed(() => !this.standingAiConsentApplies());
  /** What actually authorises an upload: the box, or the standing consent. */
  protected readonly aiConsentOk = computed(
    () => this.aiConsent() || this.standingAiConsentApplies(),
  );

  /**
   * The consent's state on THIS intake — null until an intake with a consent
   * is loaded. Read for one thing only: a consent withdrawn here brings the box
   * back (the standing consent does not ride over that withdrawal). Withdrawal
   * itself is not offered on the intake since 2026-09-22 — the consent is the
   * user's, given once at sign-up, and it is withdrawn on the profile page.
   */
  protected readonly aiConsentState = signal<AiConsentState | null>(null);

  /** Set when the client picks a file before ticking the box — inline prompt. */
  protected readonly consentBlocked = signal(false);

  /**
   * True once a consent row exists server-side, which LOCKS the checkbox ticked.
   *
   * Unticking would look like a withdrawal and would not be one — the consent
   * is append-only, so a withdrawal is its own act on its own endpoint
   * (FLOW-290), offered by the control below the box. Once that lands the
   * server reports `ai_processing_consent` false, this unlocks, and ticking the
   * box again writes a NEW row rather than reviving the withdrawn one.
   */
  protected readonly consentRecorded = signal(false);

  /**
   * A review state the client can still act on — the intake stays editable and
   * a returning client continues (or resubmits) the same record. SUBMITTED and
   * PROCEEDED are finished; a loaded record in those states is not continued —
   * the client starts a fresh application instead.
   */
  private isEditableStatus(s: PreApplicationReviewStatus | null): boolean {
    return s === null || s === 'OPEN' || s === 'DECLINED';
  }

  /** The form is shown (and editable) in every state except under-review / proceeded. */
  protected readonly editable = computed(() => this.isEditableStatus(this.reviewStatus()));
  /** A returning client whose intake was reopened for edits (banner + resubmit). */
  protected readonly reopened = computed(() => {
    return this.reviewStatus() === 'DECLINED';
  });

  /** Uploads grouped by bucket, for rendering one list per drop block. */
  protected readonly uploadsByType = computed(() => {
    const map = new Map<PreApplicationDocType, DocUpload<PreApplicationDocType>[]>();
    for (const dt of PRE_APP_DOCS) {
      map.set(dt.value, []);
    }
    for (const u of this.uploads()) {
      map.get(u.docType)?.push(u);
    }
    return map;
  });

  protected readonly anyInFlight = computed(() =>
    this.uploads().some((u) => u.state === 'uploading'),
  );

  /** Uploads that reached the server (have a doc) — what the summary lists. */
  protected readonly attachedDocs = computed(() => this.uploads().filter((u) => u.doc !== null));

  // --- Step 1's account review ----------------------------------------------
  //
  // <app-bank-accounts-step> owns the declared list and everything done to it.
  // What it reads off the statements stays here, because the upload and verdict
  // paths below write it.
  private readonly bankStep = viewChild(BankAccountsStepComponent);

  /** The bank-statement bucket, in upload order. */
  protected readonly bankUploads = computed(() => this.uploadsByType().get('BANK_STATEMENT') ?? []);

  /** docShortId → the declared account it's attributed to (or `null`). */
  protected readonly attributions = signal<Record<string, string | null>>({});

  /** docShortId → what the statement says about itself (null until read). */
  protected readonly faces = signal<Record<string, DetectedAccount | null>>({});

  /**
   * Re-read the documents from the server and absorb what the upload path has
   * since written on them: the verdict, the account read off the face, and the
   * attribution the derivation made. The one place a derived account reaches
   * the screen — the upload response itself is too early (the check runs off
   * the request thread).
   */
  protected refreshDocuments(): void {
    const id = this.preAppId();
    if (!id) return;
    this.preApp
      .get(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (pa) => this.absorbDocuments(pa), error: () => undefined });
  }

  private absorbDocuments(pa: PreApplication): void {
    const before = Object.values(this.attributions()).filter(Boolean).length;
    const attributions: Record<string, string | null> = {};
    const faces: Record<string, DetectedAccount | null> = {};
    for (const d of pa.documents) {
      this.applyVerdict(d.short_id, d.assess_status, d.assess_message);
      attributions[d.short_id] = d.source_account_ref ?? null;
      faces[d.short_id] = d.detected_account ?? null;
    }
    this.attributions.set(attributions);
    this.faces.set(faces);
    // An attribution the derivation made means a declaration it appended:
    // re-read the list so the new group renders with its statements.
    if (Object.values(attributions).filter(Boolean).length !== before) {
      this.bankStep()?.loadDeclarations();
    }
    this.cdr.markForCheck();
  }

  // --- Poll state ---
  // A live doc-check (extract text from S3 → call the model) can take well over
  // the old 20s budget, especially with several files queued — after which the
  // client stopped polling and the row sat on "Checking…" forever. Poll for up
  // to ~2 minutes (3s × 40), and when that runs out with a verdict still
  // pending, flag it so the UI can say "still checking" instead of a dead spinner.
  private pollSub: Subscription | null = null;
  private pollAttempts = 0;
  private static readonly POLL_INTERVAL_MS = 3000;
  private static readonly POLL_MAX_ATTEMPTS = 40;

  // Ticks the poll loop will hold its fire for while the socket is connected,
  // giving realtime a chance to deliver before we spend a request. A *connected*
  // socket is not a *delivering* socket (FLOW-156: gunicorn published to Redis
  // db0 while daphne subscribed on db1 — socket healthy, verdict never arrived,
  // row stuck on "Checking…" forever). After this head start we poll regardless,
  // so a silent delivery failure costs a few seconds, not the whole flow.
  private graceTicks = 0;
  private static readonly REALTIME_GRACE_TICKS = 2;

  /** True once polling gave up with a verdict still pending (slow AI check). */
  protected readonly verdictPollTimedOut = signal(false);

  /** The realtime topic we've subscribed to (once a pre-application exists). */
  private realtimeSub: Subscription | null = null;

  ngOnInit(): void {
    this.loadEntity();
    this.loadCurrent();
    // The URL owns the step: deep links land on the right step, and browser
    // back/forward (which only change the :step param) walk the wizard.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const raw = params.get('step');
      const step = STEP_IDS.find((s) => s === raw);
      if (step) {
        this.intercept.set(false);
        this.step.set(step);
      } else if (raw === INTERCEPT_SEGMENT) {
        // Refresh-safe: the intercept re-renders over Step 2. The effect below
        // clears it should the record turn out to hold a financial document.
        this.step.set('financials');
        this.intercept.set(true);
      } else if (raw !== null) {
        // Unknown segment — normalise to the first step without a history entry.
        this.step.set('bank');
        this.router
          .navigate(['/entity', this.entityId, 'applications', 'new', 'bank'], {
            replaceUrl: true,
            queryParamsHandling: 'preserve',
          })
          .catch(() => undefined);
      }
    });
  }

  /**
   * Fetch the business so the breadcrumb can name it. Deliberately its own
   * subscription rather than a `forkJoin` with the pre-application read (as
   * ks-detail does): this is cosmetic, so a failed or slow entity read must
   * never delay or fail the wizard's own load.
   */
  private loadEntity(): void {
    this.entityService
      .getEntity(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          this.entity.set(entity);
          this.cdr.markForCheck();
        },
        // Non-fatal: the crumb falls back to being omitted.
        error: () => undefined,
      });
  }

  /**
   * Fetch the entity's current pre-application so the page opens in the right
   * state — a fresh intake, "under review", a reopened more-info/declined
   * prompt, or the proceeded outcome. A missing/failed read falls back to a
   * fresh intake rather than blocking the applicant.
   */
  private loadCurrent(): void {
    this.preApp
      .current(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pa) => {
          // Only continue a genuinely editable intake (OPEN / DECLINED). A previously SUBMITTED or PROCEEDED record is finished —
          // hydrating it would lock the page on the "under review" / "proceeded"
          // panel with no way to start again. The client is always allowed to
          // begin a fresh application, so we ignore it and render a clean intake;
          // the POST path likewise won't reuse a submitted/proceeded record.
          if (pa && !this.startNew && this.isEditableStatus(pa.review_status)) {
            this.hydrate(pa);
            // A reload mid-check must keep watching, or Step 1 never settles.
            if (this.hasPendingVerdict()) this.ensurePolling(pa.short_id);
          }
          this.checkDoor(pa);
        },
        error: (err: unknown) => {
          // A 403 means the user can't act for this business — surface that
          // clearly instead of a usable-looking wizard whose calls all fail.
          // Any other read failure is non-fatal: fall back to a clean intake.
          if (this.isForbidden(err)) {
            this.forbidden.set(true);
            this.loading.set(false);
            this.cdr.markForCheck();
            return;
          }
          this.checkDoor(null);
        },
      });
  }

  /**
   * Ask the door (§A1.4) before showing the wizard. A referred business is told
   * so here, instead of being shown document steps whose first upload would
   * run an AI check on a file the system does not handle. Any failure of this
   * read falls back to the wizard — the server refuses what it must either way.
   */
  private checkDoor(current: PreApplication | null): void {
    this.preApp
      .door(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (outcome) => {
          if (outcome.referred) this.showReferral(outcome, current);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Show the referral and make sure a person will see it. The server routes a
   * referred record to the Ops worklist as it is created, so where no record
   * exists yet one is opened now — once: a returning client finds the same
   * record, and *Apply for funding* never opens a second referral.
   */
  private showReferral(
    outcome: PreApplicationRecommendationResult,
    current: PreApplication | null,
  ): void {
    this.referred.set(true);
    this.referralCopy.set({ headline: outcome.headline, reason: outcome.reason });
    if (current) {
      this.preAppId.set(current.short_id);
      this.reviewStatus.set(current.review_status);
      this.startedAt.set(current.created_at);
      return;
    }
    this.preApp
      .create(this.entityId, false, false, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pa) => {
          this.consumeStartNew();
          this.preAppId.set(pa.short_id);
          this.reviewStatus.set(pa.review_status);
          this.startedAt.set(pa.created_at);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => this.noteWriteError(err),
      });
  }

  /** True when an HTTP error is a 403 Forbidden (no authority for this entity). */
  private isForbidden(err: unknown): boolean {
    return (err as { status?: number })?.status === 403;
  }

  /** Seed the view (and, when editable, the form) from a loaded pre-application. */
  private hydrate(pa: PreApplication): void {
    this.reviewStatus.set(pa.review_status);
    this.clientReason.set(pa.client_decision_reason);
    this.dfIntent.set(pa.df_intent);
    this.startedAt.set(pa.created_at);
    this.aiConsent.set(pa.ai_processing_consent);
    this.consentRecorded.set(pa.ai_processing_consent);
    this.aiConsentState.set(pa.ai_consent);
    // Only rehydrate the editable form when the client can still act on it.
    // For SUBMITTED / PROCEEDED the form is hidden, so its state is moot.
    this.setPreAppId(pa.short_id);
    // The attributions already on file, so a returning client sees which
    // statement was filed against which account instead of an empty select.
    this.attributions.set(
      Object.fromEntries(pa.documents.map((d) => [d.short_id, d.source_account_ref ?? null])),
    );
    this.faces.set(
      Object.fromEntries(pa.documents.map((d) => [d.short_id, d.detected_account ?? null])),
    );
    this.uploads.set(
      pa.documents.map((d, i) => ({
        id: `${d.doc_type}-${d.short_id}-${i}`,
        docType: d.doc_type,
        fileName: d.file_name,
        state: 'done' as const,
        doc: {
          short_id: d.short_id,
          file_name: d.file_name,
          assess_status: d.assess_status,
          assess_message: d.assess_message,
        },
      })),
    );
  }

  /** Tick/untick the AI-processing consent. A recorded consent cannot be unticked. */
  protected onAiConsentChange(checked: boolean): void {
    if (this.consentRecorded()) {
      return;
    }
    this.aiConsent.set(checked);
    if (checked) {
      this.consentBlocked.set(false);
    }
  }

  protected readonly requirementLabel = requirementLabel;

  /** Short status of a checklist row from its uploads (for the row's status text). */
  protected statusFor(docType: PreApplicationDocType): 'none' | 'uploading' | 'added' {
    const ups = this.uploadsByType().get(docType) ?? [];
    if (ups.length === 0) {
      return 'none';
    }
    if (ups.some((u) => u.state === 'uploading')) {
      return 'uploading';
    }
    return 'added';
  }

  protected onFilesPicked(docType: PreApplicationDocType, files: File[]): void {
    // Nothing may be sent to the AI provider without consent. Checked here as
    // well as on the server so the client gets an inline prompt instead of a
    // failed upload — the server refusal is the one that actually holds.
    if (!this.aiConsentOk()) {
      this.consentBlocked.set(true);
      this.cdr.markForCheck();
      return;
    }
    this.consentBlocked.set(false);
    // A ticked fallback box is the sign-up question answered late: the server
    // records the standing consent from it too, and the cached user must say so.
    const ticked = this.showAiConsentBox() && this.aiConsent();
    // Uploading is the first real engagement — make sure a pre-application exists.
    const id = this.preAppId();
    if (!id) {
      this.preApp
        .create(this.entityId, this.dfIntent(), true, this.startNew)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (pa) => {
            this.consumeStartNew();
            this.setPreAppId(pa.short_id);
            this.consentRecorded.set(pa.ai_processing_consent);
            this.aiConsentState.set(pa.ai_consent);
            if (ticked) this.auth.markAiConsentGiven();
            for (const file of files) {
              this.uploadOne(pa.short_id, docType, file);
            }
          },
          error: (err) => this.noteWriteError(err),
        });
      return;
    }
    // The intake already exists. If consent was ticked after it was created —
    // on this step, or on the second upload step — it has never reached the
    // server, and every upload would be refused with a ticked box on screen.
    // Record it first, then upload.
    if (!this.consentRecorded()) {
      this.preApp
        .recordAiConsent(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (pa) => {
            this.consentRecorded.set(pa.ai_processing_consent);
            this.aiConsentState.set(pa.ai_consent);
            if (ticked) this.auth.markAiConsentGiven();
            this.cdr.markForCheck();
            for (const file of files) {
              this.uploadOne(id, docType, file);
            }
          },
          error: (err) => this.noteWriteError(err),
        });
      return;
    }
    for (const file of files) {
      this.uploadOne(id, docType, file);
    }
  }

  /**
   * Record a failed write. A 403 collapses the whole page to the "can't act
   * for this business" panel (every further call would fail the same way);
   * anything else surfaces inline and leaves the wizard usable.
   */
  private noteWriteError(err: unknown): void {
    if (this.isForbidden(err)) {
      this.forbidden.set(true);
    } else {
      this.serverError.set(readError(err));
    }
    this.cdr.markForCheck();
  }

  private uploadOne(shortId: string, docType: PreApplicationDocType, file: File): void {
    const id = `${docType}-${file.name}-${file.size}-${this.uploads().length}`;
    const pending: DocUpload<PreApplicationDocType> = {
      id,
      docType,
      fileName: file.name,
      state: 'uploading',
      doc: null,
    };
    this.uploads.update((list) => [...list, pending]);

    this.preApp
      .uploadDocument(shortId, file, docType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (doc) => {
          this.patchUpload(id, {
            state: 'done',
            doc: {
              short_id: doc.short_id,
              file_name: doc.file_name,
              assess_status: doc.assess_status,
              assess_message: doc.assess_message,
            },
          });
          // Where the check ran inline (CI, or a fast parse), the response
          // already carries the account read off the face and the derivation's
          // attribution — absorb them now; there is no later verdict to wait for.
          this.faces.update((m) => ({ ...m, [doc.short_id]: doc.detected_account ?? null }));
          if (doc.source_account_ref) {
            this.attributions.update((m) => ({ ...m, [doc.short_id]: doc.source_account_ref }));
            this.bankStep()?.loadDeclarations();
          }
          this.ensurePolling(shortId);
        },
        error: (err) => this.patchUpload(id, { state: 'error', error: uploadErrorMessage(err) }),
      });
  }

  protected removeUpload(uploadId: string): void {
    const upload = this.uploads().find((u) => u.id === uploadId);
    const preId = this.preAppId();
    if (upload?.doc && preId) {
      this.deletingIds.update((s) => new Set(s).add(uploadId));
      this.preApp
        .deleteDocument(preId, upload.doc.short_id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.uploads.update((list) => list.filter((u) => u.id !== uploadId));
            this.clearDeleting(uploadId);
          },
          error: () => this.clearDeleting(uploadId),
        });
    } else {
      this.uploads.update((list) => list.filter((u) => u.id !== uploadId));
    }
  }

  private clearDeleting(uploadId: string): void {
    this.deletingIds.update((s) => {
      const next = new Set(s);
      next.delete(uploadId);
      return next;
    });
  }

  private patchUpload(id: string, patch: Partial<DocUpload<PreApplicationDocType>>): void {
    this.uploads.update((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    this.cdr.markForCheck();
  }

  /** Patch a landed verdict onto the upload with this server doc short_id. */
  private applyVerdict(docShortId: string, status: DocAssessStatus, message: string): void {
    this.uploads.update((list) =>
      list.map((u) => {
        if (!u.doc || u.doc.short_id !== docShortId) {
          return u;
        }
        return { ...u, doc: { ...u.doc, assess_status: status, assess_message: message } };
      }),
    );
    // A verdict just landed — clear the "still checking" note if nothing's left.
    if (!this.hasPendingVerdict()) {
      this.verdictPollTimedOut.set(false);
    }
    this.cdr.markForCheck();
  }

  /**
   * Record the pre-application's id and start watching its realtime topic. The
   * one place the id is set, so every path (hydrate / first upload / submit)
   * gets verdict push without repeating the subscribe.
   */
  private setPreAppId(shortId: string): void {
    this.preAppId.set(shortId);
    this.watchRealtime(shortId);
  }

  // --- Realtime verdict push (fallback: polling below) ---
  /**
   * Subscribe to this pre-application's realtime topic so verdicts arrive by
   * push. Idempotent — safe to call whenever a pre-application id first appears
   * (upload, hydrate, or submit-created). Polling stays as the fallback and is
   * only briefly held off while the socket is connected (see {@link pollOnce}).
   */
  private watchRealtime(shortId: string): void {
    if (this.realtimeSub) {
      return;
    }
    this.realtimeSub = this.realtime
      .topic(`preapp.${shortId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ev) => {
        if (ev.event !== 'doc.assessed') {
          return;
        }
        const p = ev.payload as unknown as DocAssessedPayload;
        this.applyVerdict(p.short_id, p.assess_status as DocAssessStatus, p.assess_message);
        // The derivation runs with the check: a bank statement's landed verdict
        // means its account may now be read and attributed — fetch, don't guess.
        if (p.doc_type === 'BANK_STATEMENT') this.refreshDocuments();
      });
  }

  // --- Verdict polling (the fallback that must always be able to run) ---
  /**
   * Start the verdict poll loop. It always starts — the socket only buys a short
   * head start per {@link pollOnce}, never a permanent exemption. Deciding here
   * on `connected()` would strand the row whenever the socket is up but silent.
   */
  private ensurePolling(shortId: string): void {
    // A fresh upload resets the "timed out" flag — we're actively checking again.
    this.verdictPollTimedOut.set(false);
    if (this.pollSub) {
      return;
    }
    this.pollAttempts = 0;
    this.graceTicks = 0;
    this.pollSub = timer(
      ProductChooserComponent.POLL_INTERVAL_MS,
      ProductChooserComponent.POLL_INTERVAL_MS,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.pollOnce(shortId));
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private hasPendingVerdict(): boolean {
    return this.uploads().some(
      (u) => u.state === 'done' && (u.doc?.assess_status ?? 'PENDING') === 'PENDING',
    );
  }

  private pollOnce(shortId: string): void {
    if (!this.hasPendingVerdict()) {
      this.stopPolling();
      return;
    }
    // Head start for realtime — skip the request, but only for a couple of ticks.
    // Connection is necessary but not sufficient evidence that verdicts arrive.
    if (
      this.realtime.connected() &&
      ++this.graceTicks <= ProductChooserComponent.REALTIME_GRACE_TICKS
    ) {
      return;
    }
    if (++this.pollAttempts > ProductChooserComponent.POLL_MAX_ATTEMPTS) {
      // Ran out of attempts with a verdict still pending — the check is just
      // slow, not broken. Flag it so the row shows "still checking" rather than
      // an indefinite spinner, and stop hammering the endpoint.
      this.verdictPollTimedOut.set(true);
      this.cdr.markForCheck();
      this.stopPolling();
      return;
    }
    this.preApp
      .get(shortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pa) => {
          this.absorbDocuments(pa);
          if (!this.hasPendingVerdict()) {
            this.stopPolling();
          }
        },
        error: () => undefined,
      });
  }

  // --- What the steps read off the uploads ----------------------------------
  /** Server-confirmed uploads for one bucket (in-flight ones don't count yet). */
  private docsIn(docType: PreApplicationDocType): DocUpload<PreApplicationDocType>[] {
    return (this.uploadsByType().get(docType) ?? []).filter((u) => u.doc !== null);
  }

  // --- Debtors Finance unlock -------------------------------------------------
  /**
   * The DF track unlocks when the client has shared a debtors age analysis —
   * a fact about *their own uploads*, not the internal recommendation (N4).
   */
  protected readonly dfUnlocked = computed(() => this.docsIn('AGE_ANALYSIS').length > 0);

  // --- Submit for a decision ---
  /** Nothing mid-upload — submitting with no documents is allowed (soft gate). */
  protected readonly canSubmit = computed(() => !this.anyInFlight());

  /** Which submit track is in flight, so only the clicked button shows a spinner. */
  protected readonly submittingTrack = signal<'df' | 'ks' | null>(null);

  /**
   * Submit for a CapX decision. `withDf` records the client's Debtors Finance
   * interest (`df_intent`) alongside the submission — CapX still decides the
   * product(s); this is an interest flag Ops sees, not a product selection.
   */
  protected submit(withDf = false): void {
    if (this.isSubmitting() || !this.canSubmit()) {
      return;
    }
    this.isSubmitting.set(true);
    this.submittingTrack.set(withDf ? 'df' : 'ks');
    this.serverError.set(null);

    // The pre-application is created lazily on first engagement; if the applicant
    // reached the final step without uploading anything, create it now (carrying
    // the DF interest). An existing record only needs a PATCH when the answer
    // changed since it was created/rehydrated.
    const id = this.preAppId();
    const submit$ = id
      ? withDf !== this.dfIntent()
        ? this.preApp.setDfIntent(id, withDf).pipe(switchMap(() => this.preApp.submit(id)))
        : this.preApp.submit(id)
      : // Pass the consent through even on the zero-document path: the client may
        // have ticked the box without uploading, and Ops can still ask for an
        // assessment later. An unticked box does not block the submission.
        this.preApp.create(this.entityId, withDf, this.aiConsentOk(), this.startNew).pipe(
          tap((pa) => {
            this.consumeStartNew();
            this.setPreAppId(pa.short_id);
          }),
          switchMap((pa) => this.preApp.submit(pa.short_id)),
        );

    submit$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.submittingTrack.set(null);
        this.dfIntent.set(withDf);
        this.reviewStatus.set('SUBMITTED');
        this.clientReason.set('');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.submittingTrack.set(null);
        if (err?.error?.code === 'CIPC_NAME_CHECK') {
          this.nameToConfirm.set(err.error.detail);
          this.nameCheckWithDf = withDf;
          this.cdr.markForCheck();
          return;
        }
        this.noteWriteError(err);
      },
    });
  }

  /** DOC-93 §1, on the summary itself: *Yes* submits; *No* goes back to the number. */
  protected answerNameCheck(answer: 'YES' | 'NO'): void {
    this.nameToConfirm.set(null);
    this.entityService
      .answerNameCheck(this.entityId, answer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (answer === 'YES') this.submit(this.nameCheckWithDf);
          else this.serverError.set(CHECK_THE_NUMBER_LINE);
          this.cdr.markForCheck();
        },
        error: (err) => this.noteWriteError(err),
      });
  }

  protected goToDashboard(): void {
    void this.router.navigate(['/entity', this.entityId, 'dashboard']);
  }

  /** Add documents to the enquiry just submitted (add-only; PO ruling 2026-09-22). */
  protected goToAddDocuments(): void {
    const id = this.preAppId();
    if (!id) return;
    void this.router.navigate([
      '/entity',
      this.entityId,
      'applications',
      'submission',
      id,
      'documents',
    ]);
  }
}
